import React, { useState, useEffect, useCallback } from "react";

const DISMISS_KEY = "pwa_install_dismissed";
const DISMISS_DAYS = 15; // No volver a mostrar por 15 días si el usuario cierra

/**
 * Banner flotante que invita a instalar la PWA como app.
 * - Android/Chrome: usa beforeinstallprompt para instalar directamente.
 * - iOS Safari: muestra instrucciones manuales (Compartir → Agregar a Inicio).
 * - Desktop o ya instalada: no se muestra.
 */
const PWAInstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // No mostrar si ya está instalada como PWA (standalone)
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone === true) return; // iOS standalone

    // No mostrar en desktop
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile) return;

    // No mostrar si fue descartado recientemente
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000) return;
    }

    const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    setIsIOS(ios);

    if (ios) {
      // iOS no soporta beforeinstallprompt, mostrar guía manual
      const isSafari = /Safari/i.test(navigator.userAgent) && !/CriOS|FxiOS|Chrome/i.test(navigator.userAgent);
      if (isSafari) {
        setShowBanner(true);
      }
    } else {
      // Android: escuchar el evento
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShowBanner(true);
      };
      window.addEventListener("beforeinstallprompt", handler);
      return () => window.removeEventListener("beforeinstallprompt", handler);
    }
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === "accepted") {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setShowBanner(false);
  }, []);

  if (!showBanner) return null;

  return (
    <>
      <div style={styles.overlay} onClick={handleDismiss} />
      <div style={styles.banner}>
        <div style={styles.content}>
          <img
            src="/logo192.png"
            alt="TicketControl"
            style={styles.icon}
          />
          <div style={styles.text}>
            <strong style={{ fontSize: 14 }}>TicketControl Monitor</strong>
            <span style={{ fontSize: 12, color: "#999", marginTop: 2 }}>
              {isIOS
                ? "Agregalo a tu pantalla de inicio"
                : "Instalalo como app en tu teléfono"}
            </span>
          </div>
        </div>

        <div style={styles.actions}>
          {isIOS ? (
            <button
              style={styles.installBtn}
              onClick={() => setShowIOSGuide(true)}
            >
              Cómo instalar
            </button>
          ) : (
            <button style={styles.installBtn} onClick={handleInstall}>
              Instalar
            </button>
          )}
          <button style={styles.dismissBtn} onClick={handleDismiss}>
            Ahora no
          </button>
        </div>
      </div>

      {/* Guía iOS */}
      {showIOSGuide && (
        <>
          <div style={styles.modalOverlay} onClick={() => setShowIOSGuide(false)} />
          <div style={styles.modal}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>
              Instalar en iPhone
            </div>
            <div style={styles.step}>
              <span style={styles.stepNum}>1</span>
              <span>
                Tocá el botón <strong>Compartir</strong>{" "}
                <span style={{ fontSize: 18, verticalAlign: "middle" }}>
                  &#xFEFF;
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#007AFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "middle" }}>
                    <path d="M4 12v7a2 2 0 002 2h12a2 2 0 002-2v-7" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                </span>{" "}
                en la barra de Safari
              </span>
            </div>
            <div style={styles.step}>
              <span style={styles.stepNum}>2</span>
              <span>
                Buscá <strong>"Agregar a Inicio"</strong> (Add to Home Screen)
              </span>
            </div>
            <div style={styles.step}>
              <span style={styles.stepNum}>3</span>
              <span>
                Tocá <strong>"Agregar"</strong> y listo
              </span>
            </div>
            <button
              style={{ ...styles.installBtn, width: "100%", marginTop: 16 }}
              onClick={() => setShowIOSGuide(false)}
            >
              Entendido
            </button>
          </div>
        </>
      )}
    </>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9998,
  },
  banner: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    background: "#1a1a2e",
    borderTop: "1px solid #333",
    borderRadius: "16px 16px 0 0",
    padding: "16px 20px",
    zIndex: 9999,
    boxShadow: "0 -4px 20px rgba(0,0,0,0.3)",
  },
  content: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 14,
  },
  icon: {
    width: 48,
    height: 48,
    borderRadius: 10,
  },
  text: {
    display: "flex",
    flexDirection: "column",
    color: "#fff",
  },
  actions: {
    display: "flex",
    gap: 10,
  },
  installBtn: {
    flex: 1,
    padding: "10px 0",
    border: "none",
    borderRadius: 10,
    background: "#4f8ef7",
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  dismissBtn: {
    flex: 1,
    padding: "10px 0",
    border: "1px solid #444",
    borderRadius: 10,
    background: "transparent",
    color: "#aaa",
    fontSize: 14,
    cursor: "pointer",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.6)",
    zIndex: 10000,
  },
  modal: {
    position: "fixed",
    bottom: "50%",
    left: "50%",
    transform: "translate(-50%, 50%)",
    background: "#1a1a2e",
    borderRadius: 16,
    padding: 24,
    width: "calc(100% - 48px)",
    maxWidth: 340,
    zIndex: 10001,
    color: "#fff",
    boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
  },
  step: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    marginBottom: 12,
    fontSize: 13,
    lineHeight: 1.5,
    color: "#ddd",
  },
  stepNum: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: "#4f8ef7",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
    flexShrink: 0,
  },
};

export default PWAInstallBanner;
